import React, { useEffect, useRef, useState } from 'react';
import { AudioTrack } from 'livekit-client';

interface MicIndicatorProps {
    audioTrack?: AudioTrack | MediaStream | null;
    className?: string;
}

const MicIndicator: React.FC<MicIndicatorProps> = ({ audioTrack, className = '' }) => {
    const [micLevel, setMicLevel] = useState(0);
    const audioContextRef = useRef<AudioContext | null>(null);
    const analyserRef = useRef<AnalyserNode | null>(null);
    const animationFrameRef = useRef<number | null>(null);

    useEffect(() => {
        const startMicLevelMonitoring = async () => {
            // Clean up any existing monitoring first
            if (animationFrameRef.current) {
                cancelAnimationFrame(animationFrameRef.current);
                animationFrameRef.current = null;
            }
            if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
                await audioContextRef.current.close();
                audioContextRef.current = null;
            }
            analyserRef.current = null;

            if (!audioTrack) {
                // No audio track, just reset level
                setMicLevel(0);
                return;
            }

            try {
                let stream: MediaStream;

                // Check if it's a LiveKit AudioTrack or MediaStream
                if (audioTrack instanceof MediaStream) {
                    stream = audioTrack;
                } else {
                    // It's a LiveKit AudioTrack
                    const mediaStreamTrack = audioTrack.mediaStreamTrack;
                    if (!mediaStreamTrack) return;
                    stream = new MediaStream([mediaStreamTrack]);
                }

                // Create audio context and analyser
                const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
                audioContextRef.current = audioContext;

                const analyser = audioContext.createAnalyser();
                analyser.fftSize = 256;
                analyser.smoothingTimeConstant = 0.8;
                analyserRef.current = analyser;

                const microphone = audioContext.createMediaStreamSource(stream);
                microphone.connect(analyser);

                // Start level detection loop
                const dataArray = new Uint8Array(analyser.frequencyBinCount);

                const detectLevel = () => {
                    if (!analyserRef.current) return;

                    analyserRef.current.getByteFrequencyData(dataArray);

                    // Calculate average volume
                    let sum = 0;
                    for (let i = 0; i < dataArray.length; i++) {
                        sum += dataArray[i];
                    }
                    const average = sum / dataArray.length;

                    // Normalize to 0-100 range with amplification
                    const normalizedLevel = Math.min(100, (average / 128) * 100 * 1.5);
                    setMicLevel(normalizedLevel);

                    animationFrameRef.current = requestAnimationFrame(detectLevel);
                };

                detectLevel();
            } catch (error) {
                console.error('Error starting mic level monitoring:', error);
            }
        };

        startMicLevelMonitoring();

        return () => {
            if (animationFrameRef.current) {
                cancelAnimationFrame(animationFrameRef.current);
                animationFrameRef.current = null;
            }
            if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
                audioContextRef.current.close();
                audioContextRef.current = null;
            }
            analyserRef.current = null;
        };
    }, [audioTrack]);

    return (
        <div className={`flex items-center gap-1 h-8 bg-black/50 px-2 py-1.5 rounded-md ${className}`}>
            {[...Array(5)].map((_, i) => (
                <div
                    key={i}
                    className="w-1 bg-green-500 rounded-full transition-all duration-75"
                    style={{
                        height: `${Math.max(8, (micLevel / 100) * 32 * (0.5 + Math.random() * 0.5))}px`,
                        opacity: micLevel > 5 ? 1 : 0.3
                    }}
                />
            ))}
        </div>
    );
};

export default MicIndicator;
